import { Control, Controller, FieldValues, Path, useWatch } from 'react-hook-form';
import Input from 'src/components/Input/Input';
import { RadioGroup, RadioGroupItem } from 'src/components/ui/radio-group';

// The Zoho email API fields: client credentials, scope and locale, and a code or refresh token
// depending on the integration method. Shared by the setup form and the settings popup, whose
// form values differ, so the fields are generic over them and name fields with a Path<T> cast.
export const ZohoEmailApiFields = <
  T extends FieldValues,
  TContext,
  TTransformed extends FieldValues,
>({
  control,
  fieldClassName,
}: {
  control: Control<T, TContext, TTransformed>;
  // Added to the fields' wrappers that have no class of their own.
  fieldClassName?: string;
}) => {
  const integrationMethod = useWatch({ control, name: 'integration_method' as Path<T> });

  return (
    <>
      <div className={fieldClassName}>
        <Controller
          control={control}
          name={'client_id' as Path<T>}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Input
              type={'text'}
              label={'Client ID'}
              placeholder={'7002.3400edkks11ff626343a98234a9ea6ad9'}
              invalidMessage={error?.message}
              value={value}
              isValid={!error?.message}
              onChange={onChange}
            />
          )}
        />
      </div>
      <div className={'content'}>
        <Controller
          control={control}
          name={'client_secret' as Path<T>}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Input
              type={'password'}
              label={'Client secret'}
              placeholder={'h899001.fhidjkkd.3728dj2664aas3637090211284hdjn5vs3kk220032'}
              invalidMessage={error?.message}
              value={value}
              isValid={!error?.message}
              onChange={onChange}
            />
          )}
        />
      </div>
      <div className={fieldClassName}>
        <Controller
          control={control}
          name={'scope' as Path<T>}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Input
              type={'text'}
              label={'Scope'}
              placeholder={'Desk.tickets.ALL,Desk.basic.READ'}
              invalidMessage={error?.message}
              value={value}
              isValid={!error?.message}
              onChange={onChange}
            />
          )}
        />
      </div>
      <div className={fieldClassName}>
        <Controller
          control={control}
          name={'server_locale' as Path<T>}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Input
              type={'text'}
              label={'Server locale'}
              placeholder={'com'}
              invalidMessage={error?.message}
              value={value}
              isValid={!error?.message}
              onChange={onChange}
            />
          )}
        />
      </div>
      <div className={'content'}>
        <Controller
          control={control}
          name={'integration_method' as Path<T>}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <RadioGroup
              onChange={onChange}
              onValueChange={onChange}
              defaultValue={value}
              className="flex flex-row mt-5 mb-2.5 gap-5"
            >
              <div className={'flex items-center gap-2'}>
                <RadioGroupItem value={'code'} id="code" />
                <label htmlFor="code" className="cursor-pointer">
                  <p className="font-medium text-base text-ebony opacity-70 hover:opacity-100 transition">
                    Code
                  </p>
                </label>
              </div>
              <div className={'flex items-center gap-2'}>
                <RadioGroupItem value={'token'} id="token" />
                <label htmlFor="token" className="cursor-pointer">
                  <p className="font-medium text-base text-ebony opacity-70 hover:opacity-100 transition">
                    Token
                  </p>
                </label>
              </div>
            </RadioGroup>
          )}
        />
      </div>
      {integrationMethod === 'code' && (
        <div className={fieldClassName}>
          <Controller
            control={control}
            name={'code' as Path<T>}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Input
                type={'text'}
                label={'Code'}
                placeholder={
                  '1453.1308edkks30cc626343a98234d9ea6ad9.be9736d58da9bamllddbbc8f68a3f4ff00'
                }
                invalidMessage={error?.message}
                value={value}
                isValid={!error?.message}
                onChange={onChange}
              />
            )}
          />
        </div>
      )}
      {integrationMethod === 'token' && (
        <div className={fieldClassName}>
          <Controller
            control={control}
            name={'refresh_token' as Path<T>}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Input
                type={'text'}
                label={'Refresh Token'}
                placeholder={
                  '1000.8bfe42342affd5435478dc30b9036009.4239d1aab6e4f32b071224a313068b37'
                }
                invalidMessage={error?.message}
                value={value}
                isValid={!error?.message}
                onChange={onChange}
              />
            )}
          />
        </div>
      )}
    </>
  );
};
